"use client";
import { DepartmentStatusCode } from "../services/department-service";
import { ATTENDANCE_MODE, ATTENDANCE_MODE_LABEL } from "../../../constants/status";
import EmptyState from "../../../components/custom/empty-state";

export interface DepartmentRow {
  id: number;
  name: string;
  user_count: number;
  description: string;
  statusCode: DepartmentStatusCode;
  parent_department_id?: number;
  created_at: string;
  updated_at: string;
  default_attendance_mode: ATTENDANCE_MODE;
}

interface DepartmentTableProps {
  rows: DepartmentRow[];
  isLoading: boolean;
  startIndex: number;
  onEdit?: (row: DepartmentRow) => void;
  onDeactivate?: (row: DepartmentRow) => void;
  onActivate?: (row: DepartmentRow) => void;
  onViewDetail?: (row: DepartmentRow) => void;
}

const DepartmentTable: React.FC<DepartmentTableProps> = ({
  rows,
  isLoading,
  startIndex,
  onEdit,
  onDeactivate,
  onActivate,
  onViewDetail,
}) => {
  if (isLoading) return <div className="p-4 text-gray-500">Đang tải...</div>;
  if (!rows.length) return <EmptyState message="Chưa có bộ phận" description="Không tìm thấy bộ phận khớp bộ lọc." />;

  return (
      <table className="min-w-full border border-gray-400 rounded-lg overflow-hidden text-sm shadow-sm">
      <thead className="bg-gray-100">
        <tr>
          <th className="border px-3 py-2 text-center">STT</th>
          <th className="border px-3 py-2 text-center">Tên bộ phận</th>
          <th className="border px-3 py-2 text-center">Mô tả</th>
          <th className="border px-3 py-2 text-center">Số Lượng Nhân viên</th>
          <th className="border px-3 py-2 text-center">Chế độ chấm công</th>
          <th className="border px-3 py-2 text-center">Tạo lúc</th>
          <th className="border px-3 py-2 text-center">Cập nhật</th>
          <th className="border px-3 py-2 text-center">Thao tác</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
           <tr key={row.id} className="hover:bg-gray-50 bg-white">
            <td className="border px-3 py-2 text-center">{startIndex + idx}</td>
            <td className="border px-3 py-2">{row.name}</td>
            <td className="border px-3 py-2">{row.description || "—"}</td>
            <td className="border px-3 py-2">{row.user_count}</td>
            <td className="border px-3 py-2 text-center">
              <span className="inline-block rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                {ATTENDANCE_MODE_LABEL[row.default_attendance_mode] ?? ATTENDANCE_MODE_LABEL[ATTENDANCE_MODE.SHIFT]}
              </span>
            </td>
            <td className="border px-3 py-2">{row.created_at}</td>
            <td className="border px-3 py-2">{row.updated_at}</td>
            <td className="border px-3 py-2">
              <div className="flex justify-center gap-2">
                {onViewDetail && (
                  <button
                    type="button"
                    onClick={() => onViewDetail(row)}
                    aria-label="Xem chi tiết"
                    className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-slate-500 text-white shadow-sm transition hover:bg-slate-600"
                  >
                    {/* eye icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                         viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                         className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round"
                            d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover/action:block">Xem</span>
                  </button>
                )}

                {onEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(row)}
                    aria-label="Sửa"
                    className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-yellow-500 text-white shadow-sm transition hover:bg-yellow-600"
                  >
                    {/* pencil */}
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                         viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                         className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round"
                            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652l-9.547
                            9.546a4.5 4.5 0 01-1.897 1.13l-3.034.909.91-3.034a4.5 4.5 0
                            011.129-1.897l9.547-9.546z"/>
                    </svg>
                    <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover/action:block">Sửa</span>
                  </button>
                )}

                {row.statusCode === DepartmentStatusCode.ACTIVE && onDeactivate && (
                  <button
                    type="button"
                    onClick={() => onDeactivate(row)}
                    aria-label="Ngưng"
                    className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-red-500 text-white shadow-sm transition hover:bg-red-600"
                  >
                    {/* stop in circle */}
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                         viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                         className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75h4.5v4.5h-4.5z"/>
                      <path strokeLinecap="round" strokeLinejoin="round"
                            d="M12 21.75c5.385 0 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25 2.25 6.615 2.25 12 6.615 21.75 12 21.75z"/>
                    </svg>
                    <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover/action:block">Ngưng</span>
                  </button>
                )}

                {row.statusCode === DepartmentStatusCode.INACTIVE && onActivate && (
                  <button
                    type="button"
                    onClick={() => onActivate(row)}
                    aria-label="Kích hoạt"
                    className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-green-500 text-white shadow-sm transition hover:bg-green-600"
                  >
                    {/* check */}
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                         viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                         className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                    </svg>
                    <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover/action:block">Kích hoạt</span>
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default DepartmentTable;
